import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { useAuth } from "../../context/authContext";
import { ROLES } from "../../constants/roles";

import { Card, PageHeader, useToast } from "../adminComponents/adminComponents";
import {
  GENDER_LABELS,
  SECTOR_LABELS,
  STAGE_LABELS,
  mockVentures,
} from "../../data/mockData";
import "./addVentures.css";

const STATUS_OPTIONS = {
  active: "Active",
  graduated: "Graduated",
  inactive: "Inactive",
};

const INITIAL_FORM = {
  name: "",
  sector: "agriculture",
  geography: "",
  cohort: "",
  genderClassification: "women_led",
  stage: "seedling",
  status: "active",
  founderName: "",
  founderEmail: "",
  founderPhone: "",
  teamSize: "",
  description: "",
  primaryProduct: "",
  beneficiariesReached: "",
  internalNotes: "",
};

export default function AddVentures() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast, ToastViewport } = useToast();
  const [form, setForm] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const venturesPath =
    user?.role === ROLES.ADMIN ? "/admin/ventures" : "/manager/ventures";

  const setField = (key) => (event) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  function validateForm() {
    if (!form.name.trim()) return "Venture name is required.";
    if (!form.sector) return "Sector is required.";
    if (!form.cohort.trim()) return "Cohort is required.";
    if (!form.genderClassification) return "Gender classification is required.";
    if (!form.stage) return "Graduation stage is required.";
    if (!form.status) return "Status is required.";
    if (!form.founderName.trim()) return "Founder name is required.";
    if (!form.founderEmail.trim()) return "Founder email is required.";
    if (!form.geography.trim()) return "Geography is required.";
    if (!form.teamSize || Number(form.teamSize) < 1) {
      return "Team size must be at least 1.";
    }
    if (form.description.length > 500) {
      return "Description cannot exceed 500 characters.";
    }
    if (form.internalNotes.length > 1000) {
      return "Internal notes cannot exceed 1000 characters.";
    }
    if (form.beneficiariesReached && Number(form.beneficiariesReached) < 0) {
      return "End customers reached cannot be negative.";
    }
    return null;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      toast({
        title: "Cannot Create Venture",
        description: error,
        tone: "danger",
      });
      return;
    }

    setIsSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 350));

    const nextId = String(
      Math.max(0, ...mockVentures.map((venture) => Number(venture.id) || 0)) +
        1,
    );

    mockVentures.push({
      id: nextId,
      name: form.name.trim(),
      founderName: form.founderName.trim(),
      founderEmail: form.founderEmail.trim(),
      founderPhone: form.founderPhone.trim() || undefined,
      sector: form.sector,
      geography: form.geography.trim(),
      cohort: form.cohort.trim(),
      stage: form.stage,
      status: form.status,
      genderClassification: form.genderClassification,
      teamSize: Number(form.teamSize),
      description: form.description.trim() || undefined,
      primaryProduct: form.primaryProduct.trim() || undefined,
      beneficiariesReached: form.beneficiariesReached
        ? Number(form.beneficiariesReached)
        : undefined,
      internalNotes: form.internalNotes.trim() || undefined,
      milestones: [],
      dataCompleteness: 0,
      lastDataUpdate: new Date().toISOString().slice(0, 10),
    });

    toast({
      title: "Venture Added",
      description: `${form.name.trim()} has been created successfully.`,
    });

    navigate(venturesPath);
  }

  return (
    <div className="adminAddVentures-root">
      <button
        type="button"
        className="admin-btn admin-btn-ghost adminAddVentures-back"
        onClick={() => navigate(venturesPath)}
      >
        <ArrowLeft className="admin-btn-icon" />
        Back to Ventures
      </button>

      <PageHeader
        title="Add Venture"
        description="Register a new venture in your portfolio."
      />

      <form className="adminAddVentures-form" onSubmit={handleSubmit}>
        <Card title="Basic Information">
          <div className="admin-grid admin-grid-2">
            <div className="adminAddVentures-field">
              <label className="admin-label">Venture Name *</label>
              <input
                className="admin-input adminAddVentures-full"
                value={form.name}
                onChange={setField("name")}
                placeholder="e.g. Green Harvest Foods"
                required
              />
            </div>

            <div className="adminAddVentures-field">
              <label className="admin-label">Sector *</label>
              <select
                className="admin-select adminAddVentures-full"
                value={form.sector}
                onChange={setField("sector")}
              >
                {Object.entries(SECTOR_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="adminAddVentures-span-2 adminAddVentures-field">
              <label className="admin-label">Description</label>
              <textarea
                className="admin-textarea adminAddVentures-full adminAddVentures-textarea"
                value={form.description}
                onChange={setField("description")}
                placeholder="Brief description of the venture's business model and activities..."
                maxLength={500}
                rows={3}
              />
              <p className="admin-muted adminAddVentures-helpText">
                {form.description.length}/500 characters
              </p>
            </div>

            <div className="adminAddVentures-field">
              <label className="admin-label">Location *</label>
              <input
                className="admin-input adminAddVentures-full"
                value={form.geography}
                onChange={setField("geography")}
                placeholder="e.g. Kampala"
                required
              />
            </div>

            <div className="adminAddVentures-field">
              <label className="admin-label">Cohort *</label>
              <input
                className="admin-input adminAddVentures-full"
                value={form.cohort}
                onChange={setField("cohort")}
                placeholder="e.g. 2026-Q2"
                required
              />
              <p className="admin-muted adminAddVentures-helpText">
                Format: YYYY-Q1/Q2/Q3/Q4
              </p>
            </div>

            <div className="adminAddVentures-field">
              <label className="admin-label">Gender Classification *</label>
              <select
                className="admin-select adminAddVentures-full"
                value={form.genderClassification}
                onChange={setField("genderClassification")}
              >
                {Object.entries(GENDER_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="adminAddVentures-field">
              <label className="admin-label">Primary Product / Service</label>
              <input
                className="admin-input adminAddVentures-full"
                value={form.primaryProduct}
                onChange={setField("primaryProduct")}
                placeholder="e.g. Organic vegetables"
              />
            </div>
          </div>
        </Card>

        <Card title="Founder Information">
          <div className="admin-grid admin-grid-2">
            <div className="adminAddVentures-field">
              <label className="admin-label">Founder Name *</label>
              <input
                className="admin-input adminAddVentures-full"
                value={form.founderName}
                onChange={setField("founderName")}
                placeholder="e.g. Grace Nakimuli"
                required
              />
            </div>

            <div className="adminAddVentures-field">
              <label className="admin-label">Team Size *</label>
              <input
                className="admin-input adminAddVentures-full"
                type="number"
                min="1"
                max="1000"
                value={form.teamSize}
                onChange={setField("teamSize")}
                required
              />
            </div>

            <div className="adminAddVentures-field">
              <label className="admin-label">Email Address *</label>
              <input
                className="admin-input adminAddVentures-full"
                type="email"
                value={form.founderEmail}
                onChange={setField("founderEmail")}
                placeholder="founder@example.com"
                required
              />
            </div>

            <div className="adminAddVentures-field">
              <label className="admin-label">Phone Number</label>
              <input
                className="admin-input adminAddVentures-full"
                value={form.founderPhone}
                onChange={setField("founderPhone")}
                placeholder="+256 700 000 000"
              />
            </div>
          </div>
        </Card>

        <Card title="Stage & Status">
          <div className="admin-grid admin-grid-2">
            <div className="adminAddVentures-field">
              <label className="admin-label">Graduation Stage *</label>
              <select
                className="admin-select adminAddVentures-full"
                value={form.stage}
                onChange={setField("stage")}
              >
                {Object.entries(STAGE_LABELS).map(([value, label], index) => (
                  <option key={value} value={value}>
                    Level {index + 1}: {label}
                  </option>
                ))}
              </select>
              <p className="admin-muted adminAddVentures-helpText">
                Seedling → Sprout → Sapling → Tree → Redwood
              </p>
            </div>

            <div className="adminAddVentures-field">
              <label className="admin-label">Status *</label>
              <select
                className="admin-select adminAddVentures-full"
                value={form.status}
                onChange={setField("status")}
              >
                {Object.entries(STATUS_OPTIONS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="adminAddVentures-span-2 adminAddVentures-field">
              <label className="admin-label">End Customers Reached</label>
              <input
                className="admin-input adminAddVentures-full"
                type="number"
                min="0"
                value={form.beneficiariesReached}
                onChange={setField("beneficiariesReached")}
                placeholder="0"
              />
              <p className="admin-muted adminAddVentures-helpText">
                Estimated number of people directly benefiting from this venture
              </p>
            </div>
          </div>
        </Card>

        <Card title="Internal Notes">
          <div className="adminAddVentures-field">
            <label className="admin-label">Notes (Internal Only)</label>
            <textarea
              className="admin-textarea adminAddVentures-full adminAddVentures-notes"
              value={form.internalNotes}
              onChange={setField("internalNotes")}
              placeholder="Add any internal notes about this venture..."
              maxLength={1000}
              rows={4}
            />
            <p className="admin-muted adminAddVentures-helpText">
              {form.internalNotes.length}/1000 characters. These notes are only
              visible to program staff.
            </p>
          </div>
        </Card>

        <div className="adminAddVentures-actions">
          <button
            type="button"
            className="admin-btn admin-btn-outline"
            onClick={() => navigate(venturesPath)}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="admin-btn admin-btn-primary"
            disabled={isSubmitting}
          >
            <Save className="admin-btn-icon" />
            {isSubmitting ? "Creating..." : "Create Venture"}
          </button>
        </div>
      </form>

      <ToastViewport />
    </div>
  );
}
